import {
  AnchoredPanelType,
  CheckBox,
  FieldSet,
  FormattedString,
  InfoBlockLayout,
  InfoBlockStatus,
  InputFormSidePanel,
  InputFormSidePanelProps,
  SimpleBuilder,
  TextInput,
  TextInputTypes,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { Label, mergeStyleSets } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { vmdbPluggableDatabaseUrl } from "../../constants/docConstants";
import { SidePanelTestIds } from "../../constants/uiConstants";
import {
  alphaNumericUnderscoreRegex,
  pdbNameMaxLength,
  startWithLetterRegex,
  validatePdbPassword,
  validateTagKey,
} from "../../helpers/validationHelper";

export enum Fields {
  PdbName = "pdbName",
  Password = "password",
  Tags = "tags",
  PasswordConfirm = "passwordConfirm",
  UnlockAdminAccount = "unlockAdminAccount",
  TdeWalletPassword = "tdeWalletPassword",
}

export enum FieldsTestIds {
  PdbName = "pdb-name",
  Password = "password",
  TagNameInput = "tag-name-input",
  TagValueInput = "tag-value-input",
  PasswordConfirm = "password-confirm",
  UnlockAdminAccount = "unlock-admin-account",
  TdeWalletPassword = "tde-wallet-password",
}

export interface VmDbPdbCreatePanelProps extends
  Pick<InputFormSidePanelProps, "componentRef" | "onClose" | "onSubmit" | "title"> {
  targetId: string;
  showTags?: boolean;
}

const classNames = mergeStyleSets({
  nameInput: { width: "50%", minWidth: "107px" },
  valueInput: { width: "50%", minWidth: "107px" },
});

export const VmDbPdbCreatePanel = ({
  targetId,
  title,
  showTags,
  componentRef,
  onSubmit,
  onClose,
}: VmDbPdbCreatePanelProps): JSX.Element => {
  const [passwordVal, setPasswordVal] = React.useState<string>();
  const [passwordConfirmVal, setPasswordConfirmVal] = React.useState<string>();
  const [unlockAccountVal, setUnlockAccountVal] = React.useState(false);
  const [key, setKey] = React.useState<string>(uniqueGUID());

  const info = {
    messageType: InfoBlockStatus.INFO,
    message: Messages.hints.tdeWalletPasswordInfo(),
    infoLayout: InfoBlockLayout.Compact,

  };

  const onChangeUnlockAccountVal = (value: boolean): void => {
    setUnlockAccountVal(value);
  };

  const onChangePassword = (value: string): void => {
    setPasswordVal(value);
    if (passwordConfirmVal !== undefined) {
      setPasswordConfirmVal(undefined);
      setKey(uniqueGUID());
    }
  };

  return (
    <InputFormSidePanel
      componentRef={componentRef}
      anchorId={targetId}
      type={AnchoredPanelType.MEDIUM}
      title={title}
      onSubmit={onSubmit}
      onClose={onClose}
      testId={SidePanelTestIds.VmDbPdbCreatePanel}
    >
      <TextInput
        testId={FieldsTestIds.PdbName}
        required
        fieldName={Fields.PdbName}
        label={Messages.labels.pluggableDatabaseName()}
        tooltip={
          FormattedString(
            { inputText: Messages.hints.tooltipPluggableDatabase(vmdbPluggableDatabaseUrl) },
          ) as unknown as string
        }
        validator={(value: string) => {
          const errors: string [] = [];
          if (value && !startWithLetterRegex.test(value)) {
            errors.push(Messages.validation.nameStartChar());
          }
          if (value?.length > pdbNameMaxLength) {
            errors.push(Messages.validation.valueMaxLen(pdbNameMaxLength.toString()));
          }
          if (value && !alphaNumericUnderscoreRegex.test(value)) {
            errors.push(Messages.validation.valueAlphaNumericUnderscore());
          }
          return errors.length > 0 ? errors : undefined;
        }}
      />
      <CheckBox
        testId={FieldsTestIds.UnlockAdminAccount}
        defaultValue={unlockAccountVal}
        fieldName={Fields.UnlockAdminAccount}
        label={Messages.labels.unlockMyPDBAdminAccount()}
        tooltip={Messages.hints.unlockMyPDBAdminAccount()}
        onChange={value => {
          onChangeUnlockAccountVal(value);
        }}
      />
      {unlockAccountVal && (
        <>
          <TextInput
            testId={FieldsTestIds.Password}
            required
            defaultValue={passwordVal}
            fieldName={Fields.Password}
            textType={TextInputTypes.PASSWORD}
            label={Messages.labels.pdbAdminPassword()}
            onChange={value => { onChangePassword(value); }}
            validator={validatePdbPassword}
          />
          <TextInput
            testId={FieldsTestIds.PasswordConfirm}
            required
            key={key}
            defaultValue={passwordConfirmVal}
            fieldName={Fields.PasswordConfirm}
            label={Messages.labels.pdbAdminPasswordConfirm()}
            textType={TextInputTypes.PASSWORD}
            onChange={value => { setPasswordConfirmVal(value); }}
            validator={(value: string) => {
              if (value && (value !== passwordVal)) {
                return [Messages.validation.passwordConfirmation()];
              }
              return undefined;
            }}
          />
        </>
      )}
      <TextInput
        testId={FieldsTestIds.TdeWalletPassword}
        required
        fieldName={Fields.TdeWalletPassword}
        textType={TextInputTypes.PASSWORD}
        label={Messages.labels.tdeWalletPassword()}
        statusInfo={info}
      />
      {showTags && (
        <FieldSet
          header={Messages.labels.tags()}
        >
          <SimpleBuilder fieldName={Fields.Tags}>
            <TextInput
              testId={FieldsTestIds.TagNameInput}
              label={Messages.common.name()}
              fieldName="name"
              className={classNames.nameInput}
              validator={validateTagKey}
            />
            <Label>:</Label>
            <TextInput
              testId={FieldsTestIds.TagValueInput}
              label={Messages.common.value()}
              fieldName="value"
              className={classNames.valueInput}
            />
          </SimpleBuilder>
        </FieldSet>
      )}
    </InputFormSidePanel>
  );
};
